// Updated Checklist component - hide preview button for PDFs
import React, { useEffect, useState } from "react";
import { searchFilesByModule } from "../../services/FilesService/FilesService";
import toast from "react-hot-toast";
import SimpleLoader from "../../components/Loader/SimpleLoader";
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  EyeIcon,
} from "@heroicons/react/24/solid";
import { useFilePreview } from "../../hooks/useFilePreview";
import FilePreviewModal from "../../components/FilePreviewModal/FilePreviewModal";
import { downloadFile } from "../../utils/fileUtils";

const Checklist = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingFile, setDownloadingFile] = useState(null);

  // Use the reusable file preview hook
  const {
    isPreviewOpen,
    previewUrl,
    isLoading: isPreviewLoading,
    openPreview,
    closePreview,
  } = useFilePreview();

  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await searchFilesByModule({
          module_code: "checklist",
        });
        if (response?.files?.length) {
          setFiles(response.files);
        } else {
          toast.error("Құжаттар табылмады.");
        }
      } catch (err) {
        toast.error("Құжаттарды жүктеу кезінде қате орын алды.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, []);

  const isPdfFile = (fileName) => {
    return fileName?.toLowerCase().endsWith(".pdf");
  };

  const handlePreview = async (file) => {
    setSelectedFile(file);
    await openPreview(file.file_url, file.file_name);
  };

  const handleDownload = async (file) => {
    setDownloadingFile(file.uuid);
    try {
      downloadFile(file.file_url, file.file_name);
      toast.success(`"${file.label || file.file_name}" жүктеу басталды!`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Файлды жүктеу кезінде қате орын алды.");
    } finally {
      setDownloadingFile(null);
    }
  };

  const truncateText = (text, maxLength = 40) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="p-6 max-w-7xl mx-auto text-center">
      <h1 className="text-3xl font-bold text-[#674DA2] mb-8">Чеклист</h1>

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <SimpleLoader className="w-10 h-10 text-[#674DA2]" />
        </div>
      ) : files.length === 0 ? (
        <p>Құжаттар табылмады.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {files.map((file) => {
            const isPdf = isPdfFile(file.file_name);

            return (
              <div
                key={file.uuid}
                className="bg-white p-4 rounded-xl shadow hover:shadow-lg hover:scale-[1.02] hover:ring-1 hover:ring-[#674DA2] transition flex flex-col min-h-[200px]"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="bg-[#674DA2] bg-opacity-10 p-2 rounded-full flex-shrink-0">
                    <DocumentTextIcon className="w-8 h-8 text-[#674DA2]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2
                      className="font-semibold text-lg text-[#674DA2] text-left leading-tight"
                      title={file.label || file.file_name}
                    >
                      {truncateText(file.label || file.file_name, 35)}
                    </h2>
                  </div>
                </div>

                <div className="text-sm text-gray-500 mb-4 text-left flex-grow">
                  <p className="break-all mb-1" title={file.file_name}>
                    {truncateText(file.file_name, 45)}
                  </p>
                  <p className="text-xs text-gray-400">
                    ({file?.file_size_mb?.toFixed(2)} MB)
                  </p>
                  {isPdf && (
                    <span className="inline-block mt-1 text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                      PDF
                    </span>
                  )}
                </div>

                {/* Conditional action buttons */}
                {isPdf ? (
                  // PDF files: Only download button, full width
                  <button
                    onClick={() => handleDownload(file)}
                    disabled={downloadingFile === file.uuid}
                    className="w-full bg-[#674DA2] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#5a4291] transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm mt-auto"
                  >
                    {downloadingFile === file.uuid ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <ArrowDownTrayIcon className="w-4 h-4" />
                    )}
                    Жүктеу
                  </button>
                ) : (
                  // Non-PDF files: Both preview and download buttons
                  <div className="flex gap-2 mt-auto">
                    <button
                      onClick={() => handlePreview(file)}
                      disabled={isPreviewLoading}
                      className="flex-1 bg-[#674DA2] text-white px-3 py-2 rounded-md font-semibold hover:bg-[#5a4291] transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {isPreviewLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <EyeIcon className="w-4 h-4" />
                      )}
                      Көру
                    </button>

                    <button
                      onClick={() => handleDownload(file)}
                      disabled={downloadingFile === file.uuid}
                      className="flex-1 bg-[#674DA2] text-white px-3 py-2 rounded-md font-semibold hover:bg-[#5a4291] transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {downloadingFile === file.uuid ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      )}
                      Жүктеу
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* File Preview Modal - only for non-PDF files */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        previewUrl={previewUrl}
        onClose={closePreview}
        title={
          selectedFile
            ? `${selectedFile.label || selectedFile.file_name}`
            : "Алдын ала қарау"
        }
      />
    </div>
  );
};

export default Checklist;
